import { Component, OnInit } from '@angular/core'
import {
  AddAutomation,
  AutomationEventTypes,
  AutomationGuard,
  AutomationHandler,
  AutomationResponse,
  AutomationServiceTypes,
  AutomationTypes,
  eventHandlerToMessageTemplateType,
  getAutomationPayload,
  incidentCreatedAITemplateSample,
  PauseAutomation,
  selectAutomationsByType,
  selectAutomationsLoaded,
  UnPauseAutomation,
  UpdateAutomations,
} from '@tokeet-frontend/automations'
import { FormBuilder, Validators } from '@angular/forms'
import { Store } from '@ngrx/store'
import { select } from '@ngrx/store'
import {
  Template,
  TemplateService,
  selectTemplatesForAutomationEvent,
  selectTemplateEntities,
  TemplateType,
  AddTemplateComplete,
  UpdateTemplateComplete,
} from '@tokeet-frontend/templates'
import {
  ConfirmDialogService,
  isSomething,
  untilDestroy,
  FormSwitchComponent,
  ConfirmDialogStatus,
  ActionFailed,
  Destroyable,
  DataCheckerService,
  validateForm,
  selectUsersByRole,
  USER_ROLES,
  User,
} from '@tokeet-frontend/tv3-platform'
import { StripHtmlPipe } from '@tokeet-frontend/tv3-platform'
import * as lodash from 'lodash'
import { map, tap, filter } from 'rxjs/operators'
import { of, combineLatest } from 'rxjs'
import { TemplateGuard } from '@tv3/guards/template.guard'

@Component({
  selector: 'app-ai-incident-notification',
  templateUrl: './ai-incident-notification.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class AiIncidentNotificationComponent extends Destroyable implements OnInit {
  form = this.fb.group({
    status: [false],
    handler: [AutomationHandler.SendEmailMessage, [Validators.required]],
    users: [[], [Validators.required]],
  })

  event = AutomationEventTypes.IncidentCreatedAI

  handlers = AutomationHandler
  template = incidentCreatedAITemplateSample

  automations: AutomationResponse[] = []
  templatesByType: Record<string, Template> = {}
  templatesById: Record<string, Template> = {}
  admins: User[] = []
  isAutomationLoaded$ = this.store.pipe(select(selectAutomationsLoaded))

  constructor(
    private store: Store<any>,
    private templateService: TemplateService,
    private confirm: ConfirmDialogService,
    private fb: FormBuilder,
    private dataChecker: DataCheckerService
  ) {
    super()
    dataChecker.check([AutomationGuard, TemplateGuard])
  }

  ngOnInit(): void {
    const automation$ = this.store.pipe(
      select(selectAutomationsByType(AutomationTypes.Incident, false)),
      map((items) => items.filter((t) => t.event === this.event && this.compareAutopilotId(t.attributes?.autopilot))),
      tap((items) => (this.automations = items)),
      map((items) => lodash.head(items))
    )

    automation$.pipe(filter(isSomething), untilDestroy(this)).subscribe((item) => {
      this.form.patchValue({ status: !!item?.status, handler: item?.handler, users: item?.users }, { emitEvent: false })
    })

    this.store
      .pipe(
        select(selectTemplatesForAutomationEvent(this.event)),
        map((items) => items.filter((t) => this.compareAutopilotId(t.attributes?.autopilot))),
        untilDestroy(this)
      )
      .subscribe((items) => {
        this.templatesByType = lodash.keyBy(items, (t) => t.type)
      })

    this.store.pipe(select(selectTemplateEntities), untilDestroy(this)).subscribe((data) => {
      this.templatesById = data
    })

    this.store.pipe(select(selectUsersByRole(USER_ROLES.admin.value)), untilDestroy(this)).subscribe((users) => {
      this.admins = users
    })
  }

  compareAutopilotId(autopilot: string) {
    if (!autopilot) return false
    if (autopilot === this.getDataId(AutomationHandler.SendEmailMessage)) return true
    if (autopilot === this.getDataId(AutomationHandler.SendPushNotification)) return true
    if (autopilot === this.getDataId(AutomationHandler.SendSMSMessage)) return true
    return false
  }

  isAutomationExisting() {
    return isSomething(this.automations)
  }

  onToggleNotification(checked: boolean, toggle: FormSwitchComponent) {
    const doAction = () => {
      const data = this.form.getRawValue()
      this.createOrUpdateAutomation(checked, data.handler)
    }

    if (this.isAutomationExisting()) {
      this.confirm
        .openRegular({
          title: 'Warning!',
          body: checked
            ? $localize`Message Automation for this notification will be enabled, Do you want to Proceed?`
            : $localize`Message Automation for this notification will be disabled, Do you want to Proceed?`,
        })
        .subscribe((status) => {
          if (status === ConfirmDialogStatus.Confirmed) {
            doAction()
          } else {
            toggle.checked = !checked
            this.form.get('status').setValue(!checked)
          }
        })
    } else if (checked) {
      this.confirm
        .openRegular({
          title: $localize`Warning!`,
          body: $localize`Do you want to create Message Automation for this Notification?`,
        })
        .subscribe((status) => {
          if (status === ConfirmDialogStatus.Confirmed) {
            doAction()
          } else {
            this.form.get('status').setValue(false)
            toggle.checked = false
          }
        })
    }
  }

  onToggleNotificationMethod(handler: AutomationHandler) {
    const data = this.form.getRawValue()
    this.createOrUpdateAutomation(data.status, handler, true)
  }

  createOrUpdateAutomation(status: boolean, handler: AutomationHandler, isHandlerChanged = false) {
    if (this.isAutomationExisting()) {
      if (isHandlerChanged) {
        this.updateAutomation(handler)
      } else {
        const ids = this.automations.map((t) => t.pkey)
        this.store.dispatch(status ? UnPauseAutomation({ ids }) : PauseAutomation({ ids }))
      }
    } else if (status) {
      this.createAutomation(handler)
    }
  }

  normalizeTemplateContent(content: string, type: TemplateType) {
    if (type !== TemplateType.Email) {
      // remove html tags
      return new StripHtmlPipe().transform(content)
    } else {
      return lodash.replace(content, '\n', '<br>')
    }
  }

  tryToCreateTemplate(type: TemplateType, handler: AutomationHandler) {
    if (this.templatesByType?.[type]) {
      return of(this.templatesByType[type])
    }
    return this.templateService
      .add(
        {
          ...this.template,
          body: this.normalizeTemplateContent(this.template.body, type),
          type,
          event: this.event,
          attributes: {
            autopilot: this.getDataId(handler),
          },
        },
        { silent: true }
      )
      .pipe(tap((template) => this.store.dispatch(AddTemplateComplete({ template }))))
  }

  getDataId(handler: AutomationHandler) {
    return `autopilot-notify-handler|${AutomationServiceTypes.Advance}|${AutomationTypes.Incident}|${AutomationEventTypes.IncidentCreatedAI}|${handler}|`
  }

  tryToUpdateTemplate(templateId: string, type: TemplateType, handler: AutomationHandler) {
    const template = this.templatesById[templateId]
    if (!template || template.type === type) return of(null)
    return this.templateService
      .update(
        templateId,
        {
          name: template.name,
          subject: template.subject,
          description: template.description,
          body: this.normalizeTemplateContent(template.body, type),
          type,
          event: this.event,
          attributes: {
            autopilot: this.getDataId(handler),
          },
        },
        { silent: true }
      )
      .pipe(tap((data) => this.store.dispatch(UpdateTemplateComplete({ update: { id: templateId, changes: data } }))))
  }

  updateAutomation(handler: AutomationHandler) {
    const templateIds = this.automations.map((t) => t.template)
    combineLatest(
      templateIds.map((id) => this.tryToUpdateTemplate(id, eventHandlerToMessageTemplateType(handler), handler))
    ).subscribe(() => {
      this.store.dispatch(
        UpdateAutomations({
          items: this.automations.map((t) => ({
            pkey: t.pkey,
            service: AutomationServiceTypes.Advance,
            type: AutomationTypes.Incident,
            handler,
            event: t.event,
            name: t.attributes?.name,
            users: this.form.get('users').value || this.admins.map((t) => t.id),
            template: t.template,
            channel: t.settings.channel,
            rental: t.settings.rental,
          })),
        })
      )
    })
  }

  createAutomation(handler: AutomationHandler) {
    this.tryToCreateTemplate(eventHandlerToMessageTemplateType(handler), handler).subscribe(
      (template) => {
        this.store.dispatch(
          AddAutomation({
            payload: getAutomationPayload({
              service: AutomationServiceTypes.Advance,
              type: AutomationTypes.Incident,
              handler,
              event: this.event,
              status: true,
              name: this.template.name,
              guest: false,
              users: this.form.get('users').value || this.admins.map((t) => t.id),
              template: template.id,
              channels: [null],
              rentals: [null],
              attributes: {
                autopilot: this.getDataId(handler),
              },
            }),
          })
        )
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }

  isValid() {
    return !this.form.get('status').value || validateForm(this.form)
  }

  save() {
    if (!this.isAutomationExisting() || !this.form.get('status').value || !this.isValid()) {
      return
    }
    const data = this.form.getRawValue()
    this.updateAutomation(data.handler)
  }
}
